import { useEffect, useRef, useState } from "react";
import { RotateCw, FlipVertical2, QrCode, Loader2 } from "lucide-react";
import { DuckScene } from "./DuckScene";
import { ErrorBoundary } from "./ErrorBoundary";
import { FullscreenViewer } from "./FullscreenViewer";
import { ImageLightbox } from "./ImageLightbox";
import { FrontImage } from "./FrontImage";
import api, { modelUrl, modelHeaders, frontImage, blankOnError } from "../lib/api";

// The spinning 3D viewer with a real loading progress state (GLTFLoader byte-progress), a 2D fallback if the mesh
// cannot be shown, a "Repainting…" overlay while the async repaint runs, its auto-spin toggle, the "underside preview"
// flip (what the flat bottom will actually print like, QR and all), orbit hint, fullscreen button and the lightbox.
export const DuckCanvasPanel = ({ creation, height, repainting = false }) => {
  const [spin, setSpin] = useState(true);
  const [underside, setUnderside] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [qrCheck, setQrCheck] = useState(null);

  // Cache-bust the model when it was repainted (geometry is unchanged, but the single-colour base may differ).
  const url = modelUrl(creation.id, creation.repainted_at || creation.built_at || creation.version);
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [pct, setPct] = useState(0);
  const [slow, setSlow] = useState(false);
  const urlRef = useRef(url);

  // Only reset on an ACTUAL url change (a repaint cache-bust / new duck) — never on the first mount, where it would
  // race the mesh's own onLoaded and flip the state straight back to "loading".
  useEffect(() => {
    if (urlRef.current !== url) {
      urlRef.current = url;
      setPhase("loading");
      setPct(0);
    }
  }, [url]);

  // "Still loading" note after 15s of a slow / large-mesh load.
  useEffect(() => {
    if (phase !== "loading") return undefined;
    setSlow(false);
    const t = setTimeout(() => setSlow(true), 15000);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    let alive = true;
    if (!underside) return undefined;
    api
      .get(`/creations/${creation.id}/qr-bottom`, { params: height ? { height } : {} })
      .then(({ data }) => alive && setQrCheck(data))
      .catch(() => alive && setQrCheck(null));
    return () => {
      alive = false;
    };
  }, [underside, creation.id, height]);

  const qrOn = !!creation.print_options?.qr_bottom;
  const qrNote = !qrOn
    ? "QR code is off for this duck — switch it on below to print it here."
    : qrCheck && !qrCheck.available
      ? qrCheck.reason
      : qrCheck
        ? `QR printed at ${qrCheck.module_mm} mm modules — mirrored, so it reads when you flip the duck over.`
        : "Checking the QR fit…";

  return (
    <>
      <FullscreenViewer
        testId="duck-3d-canvas"
        baseClassName="duck-canvas-wrap rounded-3xl border border-border overflow-hidden h-[460px] sm:h-[560px] relative"
      >
        {phase === "error" ? (
          <div data-testid="duck-3d-fallback" className="absolute inset-0 flex items-center justify-center bg-[#f6f4ef]">
            <FrontImage creation={creation} alt="approved render" className="max-h-full max-w-full object-contain" />
            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-background/90 backdrop-blur border border-border px-4 py-1.5 text-xs text-muted-foreground">
              3D preview unavailable — showing your approved render
            </p>
          </div>
        ) : (
          <ErrorBoundary resetKey={url} onError={() => setPhase("error")}>
            <DuckScene
              url={url}
              headers={modelHeaders()}
              spin={spin}
              underside={underside}
              onProgress={setPct}
              onLoaded={() => setPhase("ready")}
              onError={() => setPhase("error")}
            />
          </ErrorBoundary>
        )}

        {phase === "loading" && (
          <div data-testid="duck-3d-loading" className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/80 backdrop-blur-sm">
            <Loader2 className="h-8 w-8 text-accent animate-spin" />
            <p className="text-sm font-semibold">Loading 3D model…</p>
            <div className="h-1.5 w-40 overflow-hidden rounded-full bg-muted">
              <div data-testid="duck-3d-loading-bar" className="h-full rounded-full bg-accent transition-[width] duration-200" style={{ width: `${pct}%` }} />
            </div>
            <span data-testid="duck-3d-loading-pct" className="text-xs font-mono2 text-muted-foreground">{pct}%</span>
            {slow && (
              <p data-testid="duck-3d-loading-slow" className="text-xs text-muted-foreground max-w-[16rem] text-center">
                Still loading — a detailed duck can take a few seconds on a slower connection.
              </p>
            )}
          </div>
        )}

        {repainting && (
          <div data-testid="duck-3d-repainting" className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/85 backdrop-blur-sm">
            <Loader2 className="h-8 w-8 text-accent animate-spin" />
            <p className="text-sm font-bold">Repainting…</p>
            <p className="text-xs text-muted-foreground max-w-[16rem] text-center">Re-mixing the filament colours from your render. This can take a minute.</p>
          </div>
        )}

        {phase !== "error" && (
          <>
            {underside ? (
              <div data-testid="underside-preview-banner" className="absolute top-4 left-1/2 -translate-x-1/2 w-[min(92%,30rem)] rounded-2xl bg-background/90 backdrop-blur border border-border px-4 py-2.5 text-center">
                <p className="text-sm font-bold">Underside preview — as it will print</p>
                <p data-testid="underside-qr-note" className="text-xs text-muted-foreground mt-0.5 flex items-center justify-center gap-1.5">
                  <QrCode className="h-3.5 w-3.5 shrink-0" /> {qrNote}
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setLightbox(true)}
                data-testid="render-thumb"
                aria-label="View original image"
                className="absolute top-4 left-4 h-20 w-20 rounded-2xl overflow-hidden border-2 border-background shadow-lg hover:ring-2 hover:ring-accent transition-shadow"
              >
                <img src={frontImage(creation)} alt="approved render" onError={blankOnError} className="h-full w-full object-cover" />
              </button>
            )}
            <div className="absolute bottom-4 left-4 flex flex-wrap items-center gap-2">
              {!underside && (
                <button
                  onClick={() => setSpin((s) => !s)}
                  data-testid="toggle-spin-btn"
                  className="flex items-center gap-2 rounded-full bg-background/80 backdrop-blur px-4 py-2 text-sm font-medium border border-border hover:bg-background"
                >
                  <RotateCw className={`h-4 w-4 ${spin ? "text-accent" : ""}`} />
                  {spin ? "Auto-spin on" : "Auto-spin off"}
                </button>
              )}
              <button
                onClick={() => setUnderside((u) => !u)}
                data-testid="toggle-underside-btn"
                className="flex items-center gap-2 rounded-full bg-background/80 backdrop-blur px-4 py-2 text-sm font-medium border border-border hover:bg-background"
              >
                <FlipVertical2 className={`h-4 w-4 ${underside ? "text-accent" : ""}`} />
                {underside ? "Back to the duck" : "Underside preview"}
              </button>
            </div>
            <span className="absolute bottom-4 right-4 hidden sm:inline rounded-full bg-background/80 backdrop-blur px-3 py-1.5 text-xs font-mono2 border border-border">
              drag to orbit · scroll to zoom
            </span>
          </>
        )}
      </FullscreenViewer>
      <ImageLightbox open={lightbox} onOpenChange={setLightbox} images={[frontImage(creation)].filter(Boolean)} alt="original render" />
    </>
  );
};
